import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { RotateCcw } from "lucide-react";
import { api } from "@/lib/api";
import PropertyCard from "@/components/PropertyCard";
import { Reveal } from "@/components/Reveal";
import { FILTER_OPTIONS, FilterSelect } from "@/components/home/SearchFilter";

export default function Properties() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [properties, setProperties] = useState(null);

  const filters = {
    type: searchParams.get("type") || "",
    location: searchParams.get("location") || "",
    size: searchParams.get("size") || "",
    budget: searchParams.get("budget") || "",
  };

  useEffect(() => {
    setProperties(null);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
    api
      .get(`/properties?${params.toString()}`)
      .then((r) => setProperties(r.data))
      .catch(() => setProperties([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const setFilter = (key) => (value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  };

  const reset = () => setSearchParams(new URLSearchParams());

  return (
    <main data-testid="properties-page" className="pt-[72px]">
      <div className="bg-navy py-16 text-white lg:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-gold">
            <span className="h-px w-10 bg-gold" aria-hidden="true" />
            Our Listings
          </p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight sm:text-5xl">
            Available Plots in Nagpur
          </h1>
          <p className="mt-4 max-w-xl text-base text-white/70">
            Residential, commercial and investment plots near Samruddhi Mahamarg, Umred Road and Wardha Road.
          </p>
        </div>
      </div>

      <div className="border-b border-gray-100 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <FilterSelect label="Property Type" value={filters.type} onChange={setFilter("type")} testId="properties-filter-type">
              <option value="">Any Type</option>
              {FILTER_OPTIONS.type.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </FilterSelect>
            <FilterSelect label="Location" value={filters.location} onChange={setFilter("location")} testId="properties-filter-location">
              <option value="">Any Location</option>
              {FILTER_OPTIONS.location.map((l) => (
                <option key={l} value={l}>{l}</option>
              ))}
            </FilterSelect>
            <FilterSelect label="Plot Size" value={filters.size} onChange={setFilter("size")} testId="properties-filter-size">
              <option value="">Any Size</option>
              {FILTER_OPTIONS.size.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </FilterSelect>
            <FilterSelect label="Budget" value={filters.budget} onChange={setFilter("budget")} testId="properties-filter-budget">
              <option value="">Any Budget</option>
              {FILTER_OPTIONS.budget.map((b) => (
                <option key={b.value} value={b.value}>{b.label}</option>
              ))}
            </FilterSelect>
            <div className="flex items-end">
              <button
                data-testid="reset-filters-button"
                onClick={reset}
                className="flex w-full items-center justify-center gap-2 rounded-sm border border-navy/20 px-4 py-3.5 text-sm font-semibold text-navy transition-colors hover:border-navy hover:bg-navy hover:text-white"
              >
                <RotateCcw className="h-4 w-4" aria-hidden="true" />
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <p data-testid="properties-count" className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          {properties === null ? "Loading..." : `${properties.length} ${properties.length === 1 ? "property" : "properties"} found`}
        </p>

        {properties !== null && properties.length === 0 ? (
          <Reveal>
            <div data-testid="no-properties-message" className="mt-10 rounded-xl border border-dashed border-navy/20 bg-white p-14 text-center">
              <p className="font-display text-2xl text-navy">No properties match your filters</p>
              <p className="mt-2 text-sm text-muted-foreground">
                Try adjusting your search, or send us an enquiry and we will help you find a plot.
              </p>
            </div>
          </Reveal>
        ) : (
          <div className="mt-8 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {properties === null
              ? [0, 1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="h-96 animate-pulse rounded-xl bg-gray-100" aria-hidden="true" />
                ))
              : properties.map((p, i) => <PropertyCard key={p.id} property={p} index={i} />)}
          </div>
        )}
      </div>
    </main>
  );
}
